import { useEffect, useMemo } from 'react'
import { Provider } from 'react-redux'
import { BrowserRouter } from 'react-router-dom'

import 'core-components/assets/css/base.scss'
import 'core-components/assets/css/css-vars.scss'
import LocalStorageController from 'core-components/classes/services/LocalStorageController'
import { NotificationContainer, NotificationController } from 'core-components/library/popup'
import { AppLoader } from 'src/core/components/app/AppLoader'
import { ViewErrorWrapper } from 'src/core/components/app/ViewErrorWrapper'
import { Routes } from 'src/core/routes/Routes'
import { connectionStatusBus } from 'src/core/utils/GlobalEventBus'
import 'src/theme.scss'

import { AppContext, IAppContext } from './app/context/AppContext'
import { EDIT_MODE_ACCESS_ROLES_DEFAULT } from './app/context/constants'
import { ErrorBoundary } from './app/ErrorBoundary'
import { EventBusSubscriber } from './app/EventBusSubscriber'
import { useAppStore } from './app/hooks/useAppStore'
import { DefaultLoaderWrapper } from './app/loaders/DefaultLoaderWrapper'
import { IApp } from './app/models/IApp'
import { SiteBuilderWrapper } from './app/SiteBuilderWrapper'
import { Banner } from './shared/header/Banner'

import styles from './App.module.scss'

export const App = (props: IApp) => {
  LocalStorageController.configure(props.localStorageSettings)

  const { appStore, isReducersLoading } = useAppStore(props.appStore)
  const LoaderWrapperComponent = props.LoaderWrapper || DefaultLoaderWrapper

  const appContextValue: IAppContext = useMemo(() => {
    return {
      headerSettings: {
        editModeAccessRoles:
          props.headerSettings?.editModeAccessRoles || EDIT_MODE_ACCESS_ROLES_DEFAULT,
        profileChangeUserSettingsVisible:
          props.headerSettings?.profileChangeUserSettingsVisible || false,
      },
      userSettings: props.userSettings,
    }
  }, [
    props.headerSettings?.editModeAccessRoles,
    props.headerSettings?.profileChangeUserSettingsVisible,
    props.userSettings,
  ])

  useEffect(() => {
    return connectionStatusBus.subscribe('changeStatus', (event, status) => {
      if (status === 'online') {
        NotificationController.showSuccess({
          message:
            'You were offline. Please, reload application, because it could miss some important data.',
        })
      }
    })
  }, [])

  if (isReducersLoading) {
    return <AppLoader />
  }

  return (
    <Provider store={appStore}>
      <BrowserRouter basename={import.meta.env.VITE_PUBLIC_URL}>
        <AppContext.Provider value={appContextValue}>
          <ViewErrorWrapper>
            <LoaderWrapperComponent customRoutes={props.CustomRoutes}>
              <SiteBuilderWrapper
                AppWidgets={props.AppWidgets}
                AppLazyWidgets={props.AppLazyWidgets}
                AppIframeWidgetData={props.AppIframeWidgetData}
              >
                <ErrorBoundary>
                  <Routes CustomRoutes={props.CustomRoutes} />
                  <div className={styles.pinOutContainer} id="absolute_container" />
                  <Banner />
                  <NotificationContainer />
                  <EventBusSubscriber />
                </ErrorBoundary>
              </SiteBuilderWrapper>
            </LoaderWrapperComponent>
          </ViewErrorWrapper>
        </AppContext.Provider>
      </BrowserRouter>
    </Provider>
  )
}
export default App
